// 气泡样式设置面板（画布预览 + 导出 PDF 同源）
// 可调：气泡大小(scale)、颜色、描边/引线粗细(lineWidth)、引线开关、描点开关、编号字号
// 折叠式，默认收起；改动即时在画布生效。
// ★ v0.77（用户 2026-10-19）：**每张图纸各存一份**，改这里只影响当前图纸。
//   （原来存单键全局偏好，切图纸跟着走 —— 用户报的「怎么全部应用了」。）
// Sprint 6.2：全部 6 项均随「气泡 PDF」导出一起生效（所见即所得）。

import { useState } from 'react';
import { SCALE_RANGE, FONT_SCALE_RANGE } from '../lib/balloonStyle';
import type { BalloonStyle } from '../lib/balloonStyle';

interface Props {
  style: BalloonStyle;
  onChange: (s: BalloonStyle) => void;
  onReset: () => void;
  /** 本图纸是否已单独存过样式（false = 正沿用默认/旧偏好） */
  own?: boolean;
}

const SWATCHES = ['#e53935', '#1565c0', '#2e7d32', '#6a1b9a', '#ef6c00', '#000000'];

/** 0.2 -> "0.2×"，0.25 -> "0.25×"，1.8 -> "1.8×"（最多两位、去掉尾随 0） */
const fmtX = (v: number) => `${String(Number(v.toFixed(2)))}×`;

export function BalloonStylePanel({ style, onChange, onReset, own = false }: Props) {
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<BalloonStyle>) => onChange({ ...style, ...patch });

  return (
    <div className={`bs-wrap ${open ? 'open' : ''}`}>
      <button className="btn sm bs-toggle"
              title="气泡样式：大小/颜色/粗细/引线/描点。★ 只对当前图纸有效，换图纸各是各的"
              onClick={() => setOpen(o => !o)}>
        样式{open ? ' ▴' : ' ▾'}
      </button>

      {open && (
        <div className="bs-panel" onClick={e => e.stopPropagation()}>
          <div className="bs-head">
            <span>气泡样式（仅本图纸 · 导出一致）</span>
            <button className="bs-reset" title="恢复出厂默认（只影响当前图纸）" onClick={onReset}>重置</button>
          </div>
          {/* v0.77：把「作用范围」写在面板里 —— 用户就是因为不知道它是全局的才报的 bug。
              不给字面提示，界面上任何地方都看不出「这一格改的是全厂」还是「只这张图」。 */}
          <div className="bs-scope">
            {own ? '当前图纸已单独设置（其它图纸不受影响）'
                 : '当前图纸沿用默认样式，改动只保存到这张图纸'}
          </div>

          {/* 大小 */}
          <div className="bs-row">
            <span className="bs-label">大小</span>
            <input type="range" min={SCALE_RANGE.min} max={SCALE_RANGE.max} step={SCALE_RANGE.step}
                   value={style.scale}
                   onChange={e => set({ scale: Number(e.target.value) })}
                   title={`气泡大小 ${fmtX(style.scale)}（${SCALE_RANGE.min}–${SCALE_RANGE.max}）`} />
            <span className="bs-val">{fmtX(style.scale)}</span>
          </div>

          {/* 颜色 */}
          <div className="bs-row">
            <span className="bs-label">颜色</span>
            <div className="bs-swatches">
              {SWATCHES.map(c => (
                <button key={c} className={`sw ${style.color === c ? 'active' : ''}`}
                        style={{ background: c }}
                        onClick={() => set({ color: c })}
                        title={c} />
              ))}
            </div>
            <input type="color" value={style.color}
                   onChange={e => set({ color: e.target.value })}
                   title="自定义颜色" />
          </div>

          {/* 粗细（气泡描边 + 引线） */}
          <div className="bs-row">
            <span className="bs-label">粗细</span>
            <input type="range" min={0.5} max={4} step={0.1} value={style.lineWidth}
                   onChange={e => set({ lineWidth: Number(e.target.value) })} />
            <span className="bs-val">{style.lineWidth.toFixed(1)}</span>
          </div>

          {/* 字号 */}
          <div className="bs-row">
            <span className="bs-label">字号</span>
            <input type="range" min={FONT_SCALE_RANGE.min} max={FONT_SCALE_RANGE.max}
                   step={FONT_SCALE_RANGE.step} value={style.fontScale}
                   onChange={e => set({ fontScale: Number(e.target.value) })} />
            <span className="bs-val">{fmtX(style.fontScale)}</span>
          </div>

          {/* 引线 / 描点开关 */}
          <div className="bs-row toggles">
            <label className="bs-check">
              <input type="checkbox" checked={style.showLeader}
                     onChange={e => set({ showLeader: e.target.checked })} />
              引线
            </label>
            <label className="bs-check">
              <input type="checkbox" checked={style.showAnchor}
                     onChange={e => set({ showAnchor: e.target.checked })} />
              描点
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
